import {FC} from 'react'
import {Link} from 'react-router-dom'
import {useQuery} from 'react-query'
import clsx from 'clsx'
import {useAuth} from '../../auth'
import {isOnPremisesInstallation} from '../../auth/core/onPremisesAccess'
import {hidesPayments} from '../../install-app/core/androidApp'
import {rial} from '../core/onlinePaymentApi'
import {getWallet, walletKey} from '../core/walletApi'
import {businessSettingsPath} from '../core/businessSettingsPath'
import './WalletBalanceChip.scss'

const fa = (value: number) => value.toLocaleString('fa-IR', {maximumFractionDigits: 1})

/** «۲۴۵ هزار»، «۱٫۲ میلیون»: a short amount for the header; cut down, never rounded up. */
export const compactRial = (value: number): string => {
  const amount = Math.max(0, Math.floor(value))
  const cut = (scale: number) => Math.floor((amount / scale) * 10) / 10
  if (amount >= 1e9) return `${fa(cut(1e9))} میلیارد`
  if (amount >= 1e6) return `${fa(cut(1e6))} میلیون`
  if (amount >= 1e3) return `${fa(cut(1e3))} هزار`
  return fa(amount)
}

/**
 * The selected business's wallet beside «امکانات تازه»: its balance in short (the exact amount in the tooltip),
 * amber when it is low, and a click opens «تنظیمات کسب‌وکار ← کیف پول». Not shown without a business, to a
 * support operator, on an on-premises install, or while wallets are not in use at all.
 */
export const WalletBalanceChip: FC<{itemClassName?: string}> = ({itemClassName}) => {
  const {currentUser} = useAuth()
  const companyId = Number(currentUser?.cpi || 0)
  const enabled =
    companyId > 0 && !currentUser?.support && !isOnPremisesInstallation(currentUser) && !hidesPayments()
  const wallet = useQuery(walletKey(companyId), () => getWallet(companyId), {
    enabled,
    staleTime: 60000,
    retry: false,
    refetchOnWindowFocus: false,
  })
  const data = wallet.data
  if (!enabled || !data?.available) return null

  const balance = Number(data.balance || 0)
  // Nothing to say while charging is closed, the wallet is empty and SMS is not free for the business.
  if (!data.chargeEnabled && balance === 0 && !data.smsWithoutCharge) return null

  const business = currentUser?.cpl?.find((company) => company.id === companyId)?.nm
  const tone = data.smsWithoutCharge ? 'free' : data.isLow ? 'low' : 'ok'
  const label =
    `موجودی کیف پول${business ? ` ${business}` : ''}: ${rial(balance)} ریال` +
    (data.smsWithoutCharge ? '؛ پیامک‌های لینک خریدار بدون کسر از کیف پول فرستاده می‌شود' : data.isLow ? '؛ موجودی کم است، کیف پول را شارژ کنید' : '')

  return (
    <div className={clsx('app-navbar-item', itemClassName)}>
      <Link to={businessSettingsPath(companyId, 'wallet')} className={clsx('wbc', `wbc--${tone}`)} title={label} aria-label={label}>
        <i className='las la-wallet wbc__icon' aria-hidden='true'></i>
        <span className='wbc__amount'>{compactRial(balance)}</span>
        <span className='wbc__unit'>ریال</span>
        {tone === 'low' && <span className='wbc__dot' aria-hidden='true'></span>}
      </Link>
    </div>
  )
}
